/**
 * Colour identity for the things on this page that have no colour of their own — a workspace, a
 * person, a role. Picked by hashing a STABLE key (an id, never a display name that can be renamed),
 * so a workspace keeps its tile through a rename and a role wears the same dot on every person's row
 * as it does on the Roles tab.
 *
 * Decoration only. Nothing on the page may MEAN anything by these hues: seat states (room / full /
 * over) and "No access" have reserved colours of their own, always printed next to words. That is also
 * why the palette leaves out orange (the page's accent, and the builders' own workspace), amber,
 * emerald and rose (the reserved states).
 *
 * The class strings are written out in full on purpose. Tailwind finds classes by scanning source text,
 * so a class assembled at runtime (`'from-' + hue + '-400'`) would never be generated.
 */
export interface Tone {
  /** A gradient fill for a tile or an avatar — white text on top of it reads in both themes. */
  fill: string;
  /** A small solid dot, for a role pill. */
  dot: string;
  /** A soft tinted square, for a role's icon on the Roles tab. */
  soft: string;
}

const TONES: Tone[] = [
  { fill: 'bg-gradient-to-br from-sky-400 to-blue-600', dot: 'bg-sky-500', soft: 'bg-sky-500/15 text-sky-500 ring-sky-500/25' },
  { fill: 'bg-gradient-to-br from-violet-400 to-indigo-600', dot: 'bg-violet-500', soft: 'bg-violet-500/15 text-violet-500 ring-violet-500/25' },
  { fill: 'bg-gradient-to-br from-fuchsia-400 to-pink-600', dot: 'bg-fuchsia-500', soft: 'bg-fuchsia-500/15 text-fuchsia-500 ring-fuchsia-500/25' },
  { fill: 'bg-gradient-to-br from-teal-400 to-cyan-600', dot: 'bg-teal-500', soft: 'bg-teal-500/15 text-teal-500 ring-teal-500/25' },
  { fill: 'bg-gradient-to-br from-indigo-400 to-purple-600', dot: 'bg-indigo-500', soft: 'bg-indigo-500/15 text-indigo-400 ring-indigo-500/25' },
  { fill: 'bg-gradient-to-br from-cyan-400 to-sky-600', dot: 'bg-cyan-500', soft: 'bg-cyan-500/15 text-cyan-500 ring-cyan-500/25' },
  { fill: 'bg-gradient-to-br from-pink-400 to-fuchsia-600', dot: 'bg-pink-500', soft: 'bg-pink-500/15 text-pink-500 ring-pink-500/25' },
  { fill: 'bg-gradient-to-br from-blue-400 to-violet-600', dot: 'bg-blue-500', soft: 'bg-blue-500/15 text-blue-500 ring-blue-500/25' },
];

export function toneFor(key: string): Tone {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return TONES[h % TONES.length];
}

/** "Acme Retail" → "AR", "Initech" → "IN". For a workspace's tile — a person's avatar keeps the Team
 *  page's own two-character treatment (`WorkspaceRowComponent.initials`) so the two pages agree. */
export function monogram(name: string): string {
  const words = (name || '').trim().split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w));
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
